/**
 * Recent action outcomes for Today resolved local cards (localStorage only, last N).
 */

import { trackOutcomeRecorded } from '@/lib/analytics/agentic'
import type { PicksAction } from '@/lib/picks/picksActionTypes'
import type {
  PicksActionOutcome,
  PicksActionOutcomeActionType,
  PicksActionOutcomeKind,
} from '@/lib/picks/picksActionOutcomeTypes'

const STORAGE_KEY = 'wagamour_picks_action_outcomes_v1'
const MAX_OUTCOMES = 5

const DEBUG =
  typeof process !== 'undefined' &&
  process.env.NODE_ENV !== 'production' &&
  typeof globalThis !== 'undefined' &&
  (globalThis as { localStorage?: Storage }).localStorage?.getItem(
    'DEBUG_PICKS_OUTCOME'
  ) === '1'

/** Preview/dev: verbose outcome pipeline (no localStorage flag). */
const PREVIEW_OUTCOME_DEBUG =
  typeof process !== 'undefined' && process.env.NODE_ENV !== 'production'

function readRaw(): PicksActionOutcome[] {
  if (typeof window === 'undefined') return []
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw) as unknown
    if (!Array.isArray(parsed)) return []
    return parsed.filter(isValidOutcome)
  } catch {
    return []
  }
}

function isValidOutcome(o: unknown): o is PicksActionOutcome {
  if (!o || typeof o !== 'object') return false
  const x = o as PicksActionOutcome
  return (
    typeof x.title === 'string' &&
    x.title.trim().length > 0 &&
    typeof x.kind === 'string' &&
    typeof x.actionType === 'string' &&
    typeof x.timestamp === 'string'
  )
}

function writeRaw(list: PicksActionOutcome[]): void {
  if (typeof window === 'undefined') return
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list.slice(-MAX_OUTCOMES)))
  } catch {
    /* quota / private mode */
  }
}

export type SavePicksActionOutcomeInput = Omit<
  PicksActionOutcome,
  'timestamp'
> & {
  timestamp?: string
}

export function saveOutcome(input: SavePicksActionOutcomeInput): void {
  if (typeof window === 'undefined') return
  const row: PicksActionOutcome = {
    ...input,
    timestamp: input.timestamp ?? new Date().toISOString(),
  }
  const prev = readRaw()
  const next = [row, ...prev].slice(0, MAX_OUTCOMES)
  writeRaw(next)
  if (DEBUG && typeof console !== 'undefined' && console.debug) {
    console.debug('[PicksActionOutcome] saved', {
      kind: row.kind,
      actionType: row.actionType,
      placeId: row.placeId ?? row.entityId,
    })
  }
  trackOutcomeRecorded({
    kind: row.kind,
    action_type: row.actionType,
    place_id: row.placeId,
    entity_id: row.entityId,
  })
  if (
    PREVIEW_OUTCOME_DEBUG &&
    typeof console !== 'undefined' &&
    console.debug
  ) {
    try {
      const ok = localStorage.getItem(STORAGE_KEY) != null
      console.debug('[PicksActionOutcome] saveOutcome', {
        storageKey: STORAGE_KEY,
        wrote: ok,
        rowCount: next.length,
      })
    } catch {
      console.debug('[PicksActionOutcome] saveOutcome', {
        storageKey: STORAGE_KEY,
        wrote: false,
        rowCount: next.length,
      })
    }
  }
}

export function getRecentOutcomes(): PicksActionOutcome[] {
  const list = readRaw()
  if (DEBUG && list.length && typeof console !== 'undefined' && console.debug) {
    console.debug('[PicksActionOutcome] read recent', { count: list.length })
  }
  return list
}

export function getMostRecentOutcome(): PicksActionOutcome | null {
  const list = readRaw()
  return list.length ? list[0] : null
}

export function clearOutcomes(): void {
  if (typeof window === 'undefined') return
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch {
    /* ignore */
  }
}

/** Small score bump when re-ranking Today winner if user recently acted on this place. */
export function getRecentOutcomeScoreBoost(
  placeId: string | undefined | null
): number {
  if (typeof window === 'undefined' || !placeId) return 0
  const list = readRaw()
  const cutoff = Date.now() - 48 * 60 * 60 * 1000
  const hit = list.find(
    (o) =>
      (o.placeId === placeId || o.entityId === placeId) &&
      new Date(o.timestamp).getTime() >= cutoff
  )
  if (!hit) return 0
  if (DEBUG && typeof console !== 'undefined' && console.debug) {
    console.debug('[PicksActionOutcome] ranking boost', {
      placeId,
      boost: 0.12,
    })
  }
  return 0.12
}

/**
 * Subtle badge fragment when the same resolved place surfaces again (premium, minimal).
 */
export function getSubtleRecentUseBadgeSuffix(
  placeId: string | undefined | null
): string | null {
  if (typeof window === 'undefined' || !placeId) return null
  const list = readRaw()
  const cutoff = Date.now() - 36 * 60 * 60 * 1000
  const hit = list.find(
    (o) =>
      (o.placeId === placeId || o.entityId === placeId) &&
      new Date(o.timestamp).getTime() >= cutoff
  )
  if (!hit) return null
  return 'Stopped by recently'
}

const RECORDABLE_TYPES: PicksActionOutcomeActionType[] = [
  'navigate',
  'ride_request',
  'call',
  'save',
  'view_details',
]

function mapActionType(
  t: PicksAction['type']
): PicksActionOutcomeActionType | null {
  if (RECORDABLE_TYPES.includes(t as PicksActionOutcomeActionType)) {
    return t as PicksActionOutcomeActionType
  }
  return null
}

function mapKind(raw: unknown): PicksActionOutcomeKind | null {
  if (raw === 'park' || raw === 'groomer' || raw === 'vet') return raw
  if (raw === 'place') return 'place'
  return null
}

/**
 * Persist outcome when action carries Today resolved local markers
 * (`buildTodayResolvedLocalCard` / Right Now when place matches `pickTodayResolvedLocalWinner`).
 */
export function maybeRecordTodayResolvedOutcome(action: PicksAction): void {
  if (typeof window === 'undefined') {
    if (PREVIEW_OUTCOME_DEBUG) {
      console.debug('[maybeRecordTodayResolvedOutcome] skip: no window')
    }
    return
  }
  if (process.env.NEXT_PUBLIC_PREVIEW_OUTCOME_DEBUG === 'true') {
    console.log('[PREVIEW DEBUG] maybeRecordTodayResolvedOutcome called', {
      type: action.type,
      placeId: action.placeId,
      payload: action.payload,
    })
  }
  const payload = action.payload as
    | {
        todayResolvedLocalKind?: unknown
        todayResolvedEntityId?: unknown
      }
    | undefined
  const actionType = mapActionType(action.type)
  if (!actionType) {
    if (PREVIEW_OUTCOME_DEBUG) {
      console.debug(
        '[maybeRecordTodayResolvedOutcome] early: action type not recordable',
        {
          type: action.type,
        }
      )
    }
    return
  }

  const kind = mapKind(payload?.todayResolvedLocalKind)
  const entityId =
    typeof payload?.todayResolvedEntityId === 'string'
      ? payload.todayResolvedEntityId
      : undefined
  if (!kind || !entityId) {
    if (PREVIEW_OUTCOME_DEBUG) {
      console.debug(
        '[maybeRecordTodayResolvedOutcome] early: missing kind or entityId',
        {
          type: action.type,
          payloadKind: payload?.todayResolvedLocalKind,
          payloadEntityId: payload?.todayResolvedEntityId,
          placeId: action.placeId,
        }
      )
    }
    return
  }

  const title = (action.title ?? '').trim()
  if (!title) {
    if (PREVIEW_OUTCOME_DEBUG) {
      console.debug('[maybeRecordTodayResolvedOutcome] early: empty title', {
        type: action.type,
        kind,
        entityId,
      })
    }
    return
  }

  const lat =
    action.lat != null && Number.isFinite(action.lat) ? action.lat : undefined
  const lng =
    action.lng != null && Number.isFinite(action.lng) ? action.lng : undefined
  if (actionType === 'navigate' || actionType === 'ride_request') {
    if (lat == null || lng == null) {
      if (PREVIEW_OUTCOME_DEBUG) {
        console.debug(
          '[maybeRecordTodayResolvedOutcome] early: navigate/ride missing lat/lng',
          {
            actionType,
            lat: action.lat,
            lng: action.lng,
          }
        )
      }
      return
    }
  }

  if (PREVIEW_OUTCOME_DEBUG) {
    console.debug('[maybeRecordTodayResolvedOutcome] recording', {
      actionType,
      kind,
      entityId,
      title,
      placeId: action.placeId,
    })
  }

  saveOutcome({
    entityId,
    placeId: action.placeId ?? entityId,
    title,
    kind,
    actionType,
    lat,
    lng,
  })
}

const PREVIEW_OUTCOME_INIT_FLAG =
  typeof process !== 'undefined' &&
  process.env.NEXT_PUBLIC_PREVIEW_OUTCOME_DEBUG === 'true'

/**
 * Preview / verification: record navigate / ride / call initiation for Right Now stacked
 * cards when Today-resolved payload is absent (or primary recorder did not persist).
 * Uses `kind: 'place'` and `action.placeId` as entity key — must match marker `stackedPlace.id`.
 */
export function maybeRecordPreviewRightNowInitiation(
  action: PicksAction
): void {
  if (typeof window === 'undefined' || !PREVIEW_OUTCOME_INIT_FLAG) return

  const payload = action.payload as
    | {
        todayResolvedEntityId?: unknown
        todayResolvedLocalKind?: unknown
      }
    | undefined
  if (
    typeof payload?.todayResolvedEntityId === 'string' &&
    mapKind(payload.todayResolvedLocalKind)
  ) {
    return
  }

  const actionType = mapActionType(action.type)
  if (!actionType) {
    if (PREVIEW_OUTCOME_INIT_FLAG) {
      console.log(
        '[PREVIEW DEBUG] maybeRecordPreviewRightNowInitiation skip type',
        {
          type: action.type,
        }
      )
    }
    return
  }

  const rawId =
    (typeof action.placeId === 'string' && action.placeId.trim()) || ''
  const entityId = rawId || undefined
  if (!entityId) {
    if (PREVIEW_OUTCOME_INIT_FLAG) {
      console.log(
        '[PREVIEW DEBUG] maybeRecordPreviewRightNowInitiation skip no placeId',
        {
          type: action.type,
          title: action.title,
        }
      )
    }
    return
  }

  const title = (action.title ?? '').trim() || 'Place'
  const lat =
    action.lat != null && Number.isFinite(action.lat) ? action.lat : undefined
  const lng =
    action.lng != null && Number.isFinite(action.lng) ? action.lng : undefined
  if (actionType === 'navigate' || actionType === 'ride_request') {
    if (lat == null || lng == null) {
      if (PREVIEW_OUTCOME_INIT_FLAG) {
        console.log(
          '[PREVIEW DEBUG] maybeRecordPreviewRightNowInitiation skip lat/lng',
          { actionType, entityId }
        )
      }
      return
    }
  }

  console.log('[PREVIEW DEBUG] maybeRecordPreviewRightNowInitiation saving', {
    entityId,
    actionType,
    title,
  })

  saveOutcome({
    entityId,
    placeId: entityId,
    title,
    kind: 'place',
    actionType,
    lat,
    lng,
  })

  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    console.log('[PREVIEW DEBUG] localStorage after save', {
      storageKey: STORAGE_KEY,
      rawLength: raw?.length ?? 0,
    })
  } catch (e) {
    console.log('[PREVIEW DEBUG] localStorage read failed', e)
  }
}
